import React from 'react';
import { Card, CardContent } from '@mui/material';
import { SITE_NAME } from 'src/config';

const Section = ({ title, children }) => (
  <div className="mt-8 first:mt-0">
    <h2 className="text-xl font-semibold text-gray-800 mb-4">{title}</h2>
    <div className="text-gray-600 space-y-2">
      {children}
    </div>
  </div>
);

const SubList = ({ items }) => (
  <ul className="list-disc ml-6 mt-2 space-y-1">
    {items.map((item, index) => (
      <li key={index} className="text-gray-600">{item}</li>
    ))}
  </ul>
);

const TermsAndConditions = () => {
  return (
    <Card>
      <CardContent>
        <div className="min-h-screen bg-gray-50 py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-xl shadow-lg p-8">
              {/* Title */}
              <h1 className="text-3xl font-bold text-gray-900 text-center mb-8">
                Terms and Conditions of Use
              </h1>

              {/* Introduction */}
              <p className="text-gray-600 mb-8 text-center">
                Welcome to mycatch! By accessing or using our website, you agree to comply
                with the following terms and conditions. Please read them carefully. If you
                do not agree with any part of these terms, you must not use our website.
              </p>

              {/* 1. General Rules */}
              <Section title="1. General Rules">
                <p>1.1 You must be at least 18 years old or have the consent of a parent or guardian to use this website.</p>
                <p>1.2 By using the website, you agree to provide accurate, truthful information and comply with all applicable laws and regulations.</p>
                <p>1.3 We reserve the right to update these terms at any time. Continued use of the site after updates constitutes acceptance of the revised terms.</p>
              </Section>

              {/* 2. Image Submission Rules */}
              <Section title="2. Image Submission Rules">
                <p>
                  <span className="font-semibold">2.1 Ownership:</span> All images uploaded must be your own original, unedited photos. You must hold all rights to the image, including the rights to its metadata.
                </p>
                <p>
                  <span className="font-semibold">2.2 Public Land:</span> Images must be taken on public land where photography is legally permitted.
                </p>
                <p>
                  <span className="font-semibold">2.3 Content Restrictions:</span>
                </p>
                <SubList
                  items={[
                    'Images must include only fish and related scenery.',
                    'Images must not include offensive, obscene, or illegal content, including but not limited to:',
                    'Nudity',
                    'Graphic violence',
                    'Hate speech or discriminatory content',
                  ]}
                />
              </Section>

              {/* 3. Consequences of Violations */}
              <Section title="3. Consequences of Violations">
                <p>3.1 Any user found violating these rules will be banned from the website without notice.</p>
                <p>3.2 Serious violations, including but not limited to uploading illegal or offensive content, will be reported to the appropriate authorities.</p>
                <p>3.3 We reserve the right to remove any image or content that violates these terms, at our sole discretion.</p>
              </Section>

              {/* 4. User Responsibilities */}
              <Section title="4. User Responsibilities">
                <p>4.1 You are solely responsible for the content you upload and its compliance with these terms.</p>
                <p>4.2 You agree not to engage in activities that disrupt or damage the website, including attempts to hack, scrape, or overload our systems.</p>
                <p>4.3 You are responsible for maintaining the confidentiality of your account credentials and for any activity that occurs under your account.</p>
              </Section>

              {/* 5. Rights and Licenses */}
              <Section title="5. Rights and Licenses">
                <p>5.1 By uploading an image, you grant us a limited, non-exclusive license to store, display, and process your image and metadata as required to provide our services.</p>
                <p>5.2 You retain ownership of your images and metadata unless sold to another party through our platform.</p>
              </Section>

              {/* 6. Metadata Sales */}
              <Section title="6. Metadata Sales">
                <p>6.1 By submitting images, you acknowledge and agree that metadata associated with your images may be sold to interested parties, subject to your explicit approval.</p>
                <p>6.2 We are not responsible for any disputes arising from metadata sales between you and the purchaser.</p>
              </Section>

              {/* 7. Limitation of Liability */}
              <Section title="7. Limitation of Liability">
                <p>7.1 We are not liable for any loss or damage resulting from:</p>
                <SubList
                  items={[
                    'Your use of the website',
                    'Unauthorized access to your account or content',
                    'Disputes arising from image or metadata submissions and sales',
                  ]}
                />
                <p className="mt-2">7.2 Our liability is limited to the fullest extent permitted by law.</p>
              </Section>

              {/* 8. Privacy and Data Protection */}
              <Section title="8. Privacy and Data Protection">
                <p>8.1 We are committed to protecting your privacy. Please review our Privacy Policy for details on how we collect, use, and store your data.</p>
                <p>8.2 Metadata is stored securely and will only be shared or sold with your explicit consent.</p>
              </Section>

              {/* 9. Termination */}
              <Section title="9. Termination">
                <p>9.1 We reserve the right to terminate your account and access to the website at any time, for any reason, including violations of these terms.</p>
              </Section>

              {/* 10. Governing Law */}
              <Section title="10. Governing Law">
                <p>10.1 These terms are governed by the laws of the UK. Any disputes will be resolved under the exclusive jurisdiction of the courts in England and Wales.</p>
              </Section>

              {/* 11. Additional Clauses */}
              <Section title="11. Disclaimer of Warranties">
                <p>
                  11.1 Your use of the website is at your sole risk. The website is provided on
                  an “as is” and “as available” basis.
                </p>
                <p>
                  11.2 We do not warrant that the website will be uninterrupted, secure, or
                  error-free; nor do we make any warranty as to the results that may be
                  obtained from its use.
                </p>
                <p>
                  11.3 We disclaim all warranties of any kind, whether express or implied,
                  including, but not limited to, the implied warranties of merchantability and
                  fitness for a particular purpose.
                </p>
              </Section>

              <Section title="12. Indemnification">
                <p>
                  12.1 You agree to indemnify, defend, and hold harmless {SITE_NAME},
                  its affiliates, directors, officers, employees, and agents from any claim or
                  demand, including reasonable attorneys’ fees, arising out of or related to:
                </p>
                <SubList
                  items={[
                    'Any content you post or share on the website',
                    'Your violation of these Terms and Conditions or any applicable law',
                    'Your infringement of any third-party right, including intellectual property or privacy rights',
                  ]}
                />
              </Section>

              <Section title="13. Third-Party Links">
                <p>
                  13.1 The website may contain links to third-party websites or services that
                  are not owned or controlled by us.
                </p>
                <p>
                  13.2 We have no control over and assume no responsibility for the content,
                  privacy policies, or practices of any third-party websites or services.
                </p>
                <p>
                  13.3 You acknowledge and agree that we shall not be responsible or liable,
                  directly or indirectly, for any damage or loss caused by or in connection
                  with the use of or reliance on any third-party content or services.
                </p>
              </Section>

              <Section title="14. Copyright Infringement (DMCA)">
                <p>
                  14.1 If you believe that any content on our website infringes your copyright,
                  please provide us with a written notice that includes:
                </p>
                <SubList
                  items={[
                    'Identification of the copyrighted work claimed to be infringed',
                    'Identification of the material that is claimed to be infringing and information reasonably sufficient to permit us to locate it',
                    'A statement by you that you have a good-faith belief that the disputed use is not authorized by the copyright owner, its agent, or the law',
                    'Your contact details (email address, telephone number, and mailing address)',
                    'A statement that the information you have provided is accurate, and that you are the copyright owner or authorized to act on the copyright owner’s behalf',
                  ]}
                />
                <p>
                  14.2 We will respond to valid notices as required by applicable law. Please
                  send notices to [DMCA Contact Email or Address].
                </p>
              </Section>

              <Section title="15. Entire Agreement & Severability">
                <p>
                  15.1 These Terms and Conditions, together with our Privacy Policy and any
                  other legal notices published on the website, constitute the entire agreement
                  between you and mycatch.
                </p>
                <p>
                  15.2 If any provision of these Terms is held to be invalid or unenforceable,
                  that provision shall be limited or eliminated to the minimum extent necessary
                  so that the remaining provisions remain in full force and effect.
                </p>
              </Section>

              <Section title="16. Fishing & Safety Disclaimers">
                <p>
                  16.1 All users are responsible for complying with local fishing laws,
                  including obtaining the proper licenses and adhering to catch or size limits.
                </p>
                <p>
                  16.2 We do not verify the accuracy of any location or species information
                  provided by users and cannot guarantee its reliability or suitability.
                </p>
                <p>
                  16.3 Always prioritize personal safety and be aware of potential hazards
                  when fishing or traveling to new locations. We are not liable for any harm
                  or accidents that may occur while following information found on the site.
                </p>
              </Section>

              <Section title="17. Changes to the Site">
                <p>
                  17.1 We reserve the right to modify, suspend, or discontinue any part of the
                  website at any time without notice.
                </p>
                <p>
                  17.2 We shall not be liable to you or any third party for any modification,
                  suspension, or discontinuation of the website or any part thereof.
                </p>
              </Section>

              {/* 18. Contact Information */}
              <Section title="18. Contact Information">
                <p>
                  If you have questions or concerns about these terms, please contact us at
                  <span className="font-semibold"> help@mycatch.co.uk</span>.
                </p>
              </Section>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TermsAndConditions;
